import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Cost pool selector dropdown component
 */
export const CostPoolSelector = ({
  formData,
  costPools,
  loadingCostPools,
  loading,
  isDevTenant,
  isSelectedTenantPartner,
  isDevResource,
  isCurrentProductFree,
  isCostPoolRequired,
  handleInputChange,
  onClose, // Optional callback to close the modal when navigating
  autoCreateCostPool = false, // New prop for auto-create state
  onAutoCreateCostPool = null, // Callback to create cost pool (for dev resources and free prices)
  creatingCostPool = false, // Whether cost pool is being created
  isFreePrice = false // Whether a free price is selected
}) => {
  const navigate = useNavigate();

  const handleNavigateToCostPool = () => {
    // Close the modal if callback is provided
    if (onClose) {
      onClose();
    }
    // Navigate to homepage with cost-pool tab and create action in URL
    navigate('/?tab=cost-pool&action=create');
  };

  // Show warning/button for dev resources if no cost pool
  const needsCostPoolForDevResource = formData.tenant_id && 
                                       costPools.length === 0 && 
                                       !loadingCostPools && 
                                       isDevResource && 
                                       !formData.cost_pool_id;

  // Show auto-create button for free prices if no cost pool
  const needsCostPoolForFreePrice = formData.tenant_id && 
                                     !formData.cost_pool_id && 
                                     !loadingCostPools && 
                                     isFreePrice && 
                                     !isDevResource && 
                                     !isDevTenant;

  // Show warning for partner tenants if no cost pool AND dev resource not selected
  const needsCostPoolForPartner = formData.tenant_id && 
                                   costPools.length === 0 && 
                                   !loadingCostPools && 
                                   !isCurrentProductFree() && 
                                   isSelectedTenantPartner && 
                                   !isDevResource && 
                                   isCostPoolRequired();
  
  // Show warning for non-partner tenants if no cost pool
  const needsCostPoolForNonPartner = formData.tenant_id && 
                                      costPools.length === 0 && 
                                      !loadingCostPools && 
                                      !isCurrentProductFree() && 
                                      !isSelectedTenantPartner && 
                                      !isDevResource && 
                                      isCostPoolRequired();
  
  const needsCostPool = needsCostPoolForPartner || needsCostPoolForNonPartner;

  return (
    <div className="form-group">
      <label htmlFor="cost_pool_id">
        Cost Pool {!isDevTenant && '*'}
      </label>
      <div className={loadingCostPools ? 'dropdown-loading' : ''}>
        <select
          id="cost_pool_id"
          name="cost_pool_id"
          value={formData.cost_pool_id ?? ''}
          onChange={handleInputChange}
          required={!isDevTenant}
          disabled={loading || loadingCostPools || isDevTenant}
          style={
            isDevTenant || autoCreateCostPool
              ? { backgroundColor: '#f5f5f5', color: '#999' } 
              : {}
          }
        >
          <option value="">
            {isDevTenant 
              ? 'Not required for development tenants' 
              : 'Select a cost pool...'
            }
          </option>
          {costPools.map(costPool => (
            <option key={costPool.id} value={costPool.id}>
              {costPool.name || costPool.id}
            </option>
          ))}
        </select>
      </div>
      {loadingCostPools && (
        <div className="loading-text">
          <div className="loading-spinner"></div>
          Loading cost pools...
        </div>
      )}
      {isDevTenant && (
        <div className="form-text text-muted">
          Cost pools not required for development tenants
        </div>
      )}
      {needsCostPoolForDevResource && (
        <div className="cost-pool-notification">
          <div className="cost-pool-notification-content">
            <p className="cost-pool-notification-text">
              Development resources require a cost pool, but it can be empty with no payment method. 
              Click below to auto-create a default cost pool for this tenant.
            </p>
            <button
              type="button"
              className="btn btn-primary cost-pool-create-button"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔘 Auto-create cost pool button clicked', { 
                  hasHandler: !!onAutoCreateCostPool, 
                  tenant_id: formData.tenant_id,
                  creatingCostPool,
                  disabled: creatingCostPool || !formData.tenant_id
                });
                if (onAutoCreateCostPool) {
                  try {
                    await onAutoCreateCostPool();
                  } catch (error) {
                    console.error('❌ Error calling onAutoCreateCostPool:', error);
                  }
                } else {
                  console.error('❌ onAutoCreateCostPool handler not provided');
                }
              }}
              disabled={creatingCostPool || !formData.tenant_id}
            >
              {creatingCostPool ? 'Creating...' : 'Auto-Create Cost Pool'}
            </button>
          </div>
        </div>
      )}
      {needsCostPoolForFreePrice && (
        <div className="cost-pool-notification">
          <div className="cost-pool-notification-content">
            <p className="cost-pool-notification-text">
              Free plans require a cost pool, but it can be empty with no payment method. 
              Click below to auto-create a default cost pool for this tenant.
            </p>
            <button
              type="button"
              className="btn btn-primary cost-pool-create-button"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔘 Auto-create cost pool button clicked for free price', { 
                  hasHandler: !!onAutoCreateCostPool, 
                  tenant_id: formData.tenant_id,
                  creatingCostPool,
                  disabled: creatingCostPool || !formData.tenant_id
                });
                if (onAutoCreateCostPool) {
                  try {
                    await onAutoCreateCostPool();
                  } catch (error) {
                    console.error('❌ Error calling onAutoCreateCostPool:', error);
                  }
                } else {
                  console.error('❌ onAutoCreateCostPool handler not provided');
                }
              }}
              disabled={creatingCostPool || !formData.tenant_id}
            >
              {creatingCostPool ? 'Creating...' : 'Auto-Create Cost Pool'}
            </button>
          </div>
        </div>
      )}
      {needsCostPool && !isDevResource && (
        <div className="cost-pool-notification">
          <div className="cost-pool-notification-content">
            <p className="cost-pool-notification-text">
              {isSelectedTenantPartner ? (
                <>
                  No cost pools with valid payment methods available for this tenant. You need to either create a cost pool with an active payment method, or enable "Development Resource" to continue.
                </>
              ) : (
                <>
                  No cost pools with valid payment methods available for this tenant. You need to create a cost pool with an active payment method to continue.
                </>
              )}
            </p>
            <button
              type="button"
              className="btn btn-primary cost-pool-create-button"
              onClick={handleNavigateToCostPool}
            >
              Create Cost Pool
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


